import type { ProjectMetadata } from '../types';

export type ProjectsLoadState = 'idle' | 'loading' | 'success' | 'error';

export const PROJECTS_LOAD_ERROR_MESSAGE = 'Your projects could not be loaded.';

export function getFirestoreDatabaseIdFromConfig(config: { firestoreDatabaseId?: string }): string {
  return config.firestoreDatabaseId || '(default)';
}

/**
 * Projects are listed from users/{authUid}/projects. Implicit ownership applies when
 * metadata omits userId/ownerId (common in older manifests). Only exclude records that
 * explicitly belong to another user.
 */
export function isProjectVisibleToUser(
  project: ProjectMetadata,
  authUid: string | undefined,
): boolean {
  if (!authUid) return false;

  const ownerId = (project as ProjectMetadata & { ownerId?: string }).ownerId;
  if (ownerId && ownerId !== authUid) return false;
  if (project.userId && project.userId !== authUid) return false;
  return true;
}

export function filterUserProjects(
  projects: ProjectMetadata[],
  authUid: string | undefined,
): ProjectMetadata[] {
  if (!authUid) return [];
  return projects.filter((project) => isProjectVisibleToUser(project, authUid));
}

export function normalizeProjectFromFirestore(
  docId: string,
  data: Record<string, unknown>,
  authUid: string,
): ProjectMetadata {
  const project = data as ProjectMetadata;
  return {
    ...project,
    id: project.id || docId,
    userId: project.userId || authUid,
  };
}

export function resolveProjectsLoadPresentation(
  loadState: ProjectsLoadState,
  projectCount: number,
): 'loading' | 'error' | 'empty' | 'list' {
  if (loadState === 'loading' || loadState === 'idle') return 'loading';
  if (loadState === 'error') return 'error';
  if (projectCount === 0) return 'empty';
  return 'list';
}
